/** @type {import('tailwindcss').Config} */

const { devConsoleLog } = require('./src/utils/logging');
const backgroundImages = require('./src/styles/backgroundImages');

module.exports = {
  content: ['./src/**/*.{js,ts,jsx,tsx}'],
  theme: {
    screens: {
      sm: '375px',
      md: '768px',
      lg: '1024px',
      xl: '1240px',
      '2xl': '1440px',
    },
    fontFamily: {
      sans: [
        'BrownPro',
        'HelveticaNeue',
        'Helvetica Neue',
        'Helvetica',
        'Arial',
        'sans-serif',
      ],
      bradford: ['BradfordLLWeb-BookItalic'],
    },
    extend: {
      colors: {
        warning: '#D52300',
        success: '#19883E',
        charcoal: '#514f4e',
        'charcoal-v2': '#424140',
        'charcoal-dark': '#3B3A39',
        'charcoal-opacity-20': '#514f4e33',
        'grey-midtone': '#9AA5AF',
        'grey-midtone-light': '#DCD7D2',
        'grey-light': '#F4F4F4',
        grey: {
          DEFAULT: '#DDD',
          dark: '#424140',
          light: '#6E757C',
          pastel: '#F4F4F4',
        },
        indigo: {
          DEFAULT: '#536DC4',
          pastel: '#F5F7FC',
          tint: '#EDF0F9',
          dark: '#3B54AB',
          'extra-dark': '#131C66',
        },
        aqua: {
          DEFAULT: '#4EDCCA',
          tint: '#EFFAF9',
        },
        'kelly-green': {
          DEFAULT: '#29CC5F',
          tint: '#EAF9F0',
          pastel: '#E5F6E3',
        },
        'lime-green': {
          DEFAULT: '#BBDC00',
          tint: '#F5FAEA',
        },
        yellow: {
          DEFAULT: '#FEDB00',
          tint: '#FCF9EB',
          pastel: '#F9F8F3',
        },
        orange: {
          DEFAULT: '#FF9955',
          tint: '#F8F4EC',
        },
        red: {
          DEFAULT: '#FF5E3E',
          tint: '#F9EFEC',
          v2: '#DB350F',
          'v2-hover': '#832009',
        },
        pink: {
          DEFAULT: '#E44C9A',
          tint: '#F7F3F7',
        },
        purple: {
          DEFAULT: '#B85BBF',
          tint: '#F7F3F7',
        },
        teal: {
          DEFAULT: '#EFFBF9',
        },
        tan: {
          DEFAULT: '#f9f7f4',
          tint: '#fffbe8',
          pastel: '#F9F7F5',
        },
        khaki: {
          tint: '#F8F6F1',
        },
        'whatsapp-green': '#25D366',
        blue: {
          tint: '#DFF5F3',
          deep: '#202EA8',
          'deep-hover': '#131C66',
          light: '#F2FAFF',
        },
        'off-white': {
          DEFAULT: '#F9F8F3',
        },
        maroon: {
          DEFAULT: '#3C001A',
          dark: '#360019',
          'dark-hover': '#20000f',
        },
      },
      fontSize: {
        h4: ['1.125rem', '1.75rem'],
        h3: ['1.25rem', '2rem'],
        h2: ['1.5rem', '2rem'],
        h1: ['2rem', '2.625rem'],
        hero: ['2rem', '2.625rem'],
        'd-sm': ['1rem', '1.25rem'],
        'd-base': ['1.125rem', '1.5rem'],
        'd-lg': ['1.25rem', '2rem'],
        'd-h4': ['1.125rem', '1.5rem'],
        'd-h3': ['1.5rem', '2rem'],
        'd-h2': ['2rem', '2.5rem'],
        'd-h1': ['2.625rem', '3.5rem'],
        'd-hero': ['3.438rem', '4.063rem'],
      },
      dropShadow: {
        le: '0px 3px 16px rgba(0, 0, 0, 0.2)',
      },
      gridTemplateColumns: {
        'explorer-slice': '1fr auto',
      },
      gridTemplateRows: {
        auto: 'auto',
      },
      // Leverages CSS grid-template-areas under the hood.
      gridTemplateAreas: {
        usSmall: [
          'learn',
          'products',
          'help',
          'gifting',
          'podcast',
          'app',
          'social',
          'selectors',
          'credentials',
        ],
        usMedium: [
          'learn help',
          'learn gifting',
          'products podcast',
          'app social',
          'selectors .',
          'credentials .',
        ],
        usLarge: [
          'learn products gifting help app',
          'learn products gifting help social',
          'learn products gifting podcast selectors',
          'learn products gifting podcast credentials',
        ],
        usXLarge: [
          'learn products gifting help podcast app',
          'learn products gifting help podcast social',
          'learn products gifting help podcast selectors',
          'learn products gifting help podcast credentials',
        ],
        auSmall: [
          'contact',
          'learn',
          'products',
          'help',
          'social',
          'selectors',
          'credentials',
        ],
        auMedium: [
          'contact .',
          'learn help',
          'products social',
          'selectors .',
          'credentials .',
        ],
        auLarge: [
          'learn products help social',
          'learn products help selectors',
          'learn products help credentials',
        ],
        auXLarge: [
          'learn products help social',
          'learn products help selectors',
          'learn products help credentials',
        ],
        euSmall: [
          'learn',
          'products',
          'help',
          'gifting',
          'podcast',
          'app',
          'social',
          'selectors',
          'credentials',
        ],
        euMedium: [
          'contact .',
          'learn help',
          'learn help',
          'products social',
          'products social',
          'selectors .',
          'credentials .',
        ],
        euLarge: [
          'learn products help contact selectors',
          'learn products help social selectors',
          'learn products help social selectors',
          'learn products help social credentials',
        ],
        euXLarge: [
          'learn products help contact selectors',
          'learn products help social selectors',
          'learn products help social selectors',
          'learn products help social credentials',
        ],
      },
      aspectRatio: {
        '3/2': '3 / 2',
      },
      borderWidth: {
        18: '18px',
      },
      backgroundImage: backgroundImages,
      transitionProperty: {
        width: 'width',
      },
      boxShadow: {
        'sticky-cta':
          'rgba(0, 0, 0, 0.12) 0px 0px 1px 0px, rgba(0, 0, 0, 0.16) 0px -2px 3px 0px',
        card: '0px 0px 5px 0px #00000059',
      },
      zIndex: {
        999: '999',
      },
      keyframes: {
        rise: {
          from: { height: '100%' },
          to: { height: '0%' },
        },
      },
      animation: {
        rise: 'rise 1s ease-out forwards',
      },
      listStyleImage: {
        purpleCircle:
          'url("//images.ctfassets.net/0sea1vycfyqy/7HhYyUuINfKc4iM17HHUp9/9fbc1056256a913c37bae5f96e05700b/Circle_01.png")',
        greenCheck:
          'url("//images.ctfassets.net/0sea1vycfyqy/6uxFIDIxdK75AZEZ5CjYj4/520ab3acee3d0f3c441f990bd33fece4/CheckMark_Brushstroke.png")',
      },
    },
  },
  plugins: [
    require('daisyui'),
    require('@savvywombat/tailwindcss-grid-areas'),
    require('@tailwindcss/aspect-ratio'),
  ],
  daisyui: {
    styled: false,
    themes: [],
    utils: true,
  },
  safelist: [
    {
      pattern: _generateSafelistPattern(),
    },
    {
      pattern: /text-(left|right|center)/,
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
    },
    {
      pattern: /justify-(start|center|end)/,
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
    },
    {
      pattern: /gap/,
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
    },
  ],
};

/**
 * Any styles that are configurable in contentful (i.e. colors, spacing) are subject to tree shaking
 * We need to inform tailwind to include the styles that our customizations could result in.
 *
 * @returns {RegExp}
 */
function _generateSafelistPattern() {
  devConsoleLog('\n💅 Tailwind Safelist Pattern 💅\n');

  const customizations = [
    {
      type: 'colors',
      prefixes: ['bg', 'border-b', 'border-t', 'stroke'],
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
      roots: [
        'white',
        'charcoal',
        'grey',
        'aqua',
        'indigo',
        'kelly-green',
        'lime-green',
        'orange',
        'yellow',
        'red',
        'pink',
        'purple',
        'tan',
        'teal',
        'khaki',
        'off-white',
      ],
    },
    {
      type: 'spacing',
      prefixes: ['gap', 'my', 'py'],
      roots: ['4', '6', '8', '10', '12'],
    },
  ];

  const customStyles = customizations.flatMap((customization) =>
    _generateCustomizationRegex(customization),
  );
  const regex = new RegExp(customStyles.join('|'));

  devConsoleLog(regex);

  return regex;
}

function _generateCustomizationRegex(customization) {
  const rootsPart = `(?=${customization.roots.join('|')})`;

  return customization.prefixes.flatMap((prefix) => `${prefix}-${rootsPart}`);
}
